import { useState, useMemo, useCallback } from 'react'
import {
  useAdminAllUsers,
  useAdminCreateUser,
  useAdminUpdateUser,
  useAdminDeleteUser,
  useAdminRestoreUser,
} from '../../hooks/useAdmin'
import { useAuthStore } from '../../store/auth'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass } from '../../components/admin/AdminModal'
import { DataTable } from '../../components/admin/DataTable'
import { Avatar } from '../../components/ui/Avatar'
import type { ColumnDef } from '@tanstack/react-table'
import type { AdminUser } from '../../types/admin'

const ROLES = ['person', 'church_admin', 'super_admin'] as const

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const roleBadge: Record<string, { bg: string; color: string }> = {
  super_admin: { bg: '#1a1a2e', color: '#fff' },
  church_admin: { bg: 'var(--primary-soft)', color: 'var(--primary)' },
  person: { bg: 'var(--surface-2)', color: 'var(--ink-2)' },
}

type UserFormState = { email: string; displayName: string; password: string; role: string }
const emptyForm = (): UserFormState => ({ email: '', displayName: '', password: '', role: 'person' })

export default function AdminUsersPage() {
  const { user: me } = useAuthStore()
  const [showDeleted, setShowDeleted] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null)
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null)
  const [form, setForm] = useState<UserFormState>(emptyForm)

  const { data, isLoading } = useAdminAllUsers({ showDeleted })
  const createUser = useAdminCreateUser()
  const updateUser = useAdminUpdateUser()
  const deleteUser = useAdminDeleteUser()
  const restoreUser = useAdminRestoreUser()

  const users = useMemo(() => data?.data ?? [], [data])

  function openCreate() { setForm(emptyForm()); setEditing('new') }

  function openEdit(user: AdminUser) {
    setForm({ email: user.email, displayName: user.displayName, password: '', role: user.role })
    setEditing(user)
  }

  function handleSave() {
    if (editing === 'new') {
      createUser.mutate(
        { email: form.email.trim(), password: form.password, displayName: form.displayName.trim(), role: form.role },
        { onSuccess: () => setEditing(null) },
      )
      return
    }
    if (!editing) return
    const body: { displayName: string; email: string; role: string; password?: string } = {
      displayName: form.displayName.trim(),
      email: form.email.trim(),
      role: form.role,
    }
    if (form.password) body.password = form.password
    updateUser.mutate({ id: editing.id, ...body }, { onSuccess: () => setEditing(null) })
  }

  const isPending = createUser.isPending || updateUser.isPending

  const openEditCb = useCallback(openEdit, [])
  const setConfirmDeleteCb = useCallback(setConfirmDelete, [])

  const columns = useMemo<ColumnDef<AdminUser, unknown>[]>(() => [
    {
      accessorKey: 'displayName',
      header: 'Name',
      meta: { label: 'Name' },
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          <Avatar name={row.original.displayName} src={row.original.avatarUrl} size={28} />
          <span className={row.original.deletedAt ? 'line-through text-muted' : 'font-medium'}>
            {row.original.displayName}
          </span>
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      meta: { label: 'Email' },
      cell: ({ getValue }) => <span className="text-ink-2">{getValue() as string}</span>,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      meta: { label: 'Role' },
      cell: ({ getValue }) => {
        const role = getValue() as string
        const badge = roleBadge[role] ?? roleBadge.person
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: badge.bg, color: badge.color }}>
            {role}
          </span>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined',
      meta: { label: 'Joined' },
      cell: ({ getValue }) => <span className="text-muted">{fmt(getValue() as string)}</span>,
      sortingFn: 'datetime',
    },
    {
      id: '_actions',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { skipExport: true },
      cell: ({ row }) => {
        const u = row.original
        const isSelf = u.id === me?.id
        return (
          <div className="flex gap-2">
            {u.deletedAt ? (
              <button
                type="button"
                onClick={() => restoreUser.mutate(u.id)}
                disabled={restoreUser.isPending}
                className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50"
              >
                Restore
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => openEditCb(u)}
                  className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink"
                >
                  Edit
                </button>
                {!isSelf && (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteCb(u)}
                    className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep"
                  >
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        )
      },
    },
  ], [me?.id, restoreUser, openEditCb, setConfirmDeleteCb])

  const toolbarSlot = (
    <div className="flex gap-2 items-center">
      <button
        type="button"
        onClick={() => setShowDeleted((v) => !v)}
        className={`h-8 px-3 rounded-lg border text-[12.5px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}
      >
        {showDeleted ? 'Hide deleted' : 'Show deleted'}
      </button>
      {!showDeleted && (
        <button
          type="button"
          onClick={openCreate}
          className="h-8 px-3.5 rounded-lg border-0 bg-[#1a1a2e] text-white text-[12.5px] font-semibold cursor-pointer font-sans"
        >
          + Add user
        </button>
      )}
    </div>
  )

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Users</h1>
        {!isLoading && <div className="text-[13px] text-muted mt-1">{users.length} total</div>}
      </div>

      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        filterable
        configurable
        exportable
        exportFilename="users"
        toolbarSlot={toolbarSlot}
        getRowId={(row) => row.id}
        emptyMessage="No users found."
        pageSize={50}
      />

      {editing && (
        <AdminModal
          title={editing === 'new' ? 'Add user' : 'Edit user'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isPending || !form.email || !form.displayName || (editing === 'new' && !form.password)}
                className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          {editing !== 'new' && (
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              UUID
              <div className="flex gap-1.5">
                <input readOnly value={editing.id} className={`${adminFieldClass()} flex-1 font-mono text-[11px] text-muted cursor-text select-all`} />
                <button type="button" onClick={() => void navigator.clipboard.writeText(editing.id)} className="px-2.5 py-1.5 rounded-lg border border-line-soft bg-s2 text-[11px] text-ink-2 cursor-pointer font-sans shrink-0">Copy</button>
              </div>
            </label>
          )}
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Display name
            <input className={adminFieldClass()} value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Email
            <input type="email" className={adminFieldClass()} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={editing !== 'new' && editing.id === me?.id} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Password {editing !== 'new' && <span className="font-normal text-muted">(leave blank to keep)</span>}
            <input type="password" className={adminFieldClass()} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Role
            <select className={adminFieldClass()} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} disabled={editing !== 'new' && editing.id === me?.id}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete user?"
          body={<><strong>{confirmDelete.displayName}</strong> ({confirmDelete.email}) will be soft-deleted. They won&apos;t be able to log in, but can be restored from the deleted users view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => deleteUser.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}
          onCancel={() => setConfirmDelete(null)}
          isPending={deleteUser.isPending}
        />
      )}
    </div>
  )
}
